import { useEffect, useState } from 'react';
import { onGetCategories } from '../util/api';
import { buildCategoryConfigMap, transformDbCategories } from '../util/data';

/**
 * Shared access to GET /api/categories.
 *
 * The raw rows are cached at module level, and every view the app needs is
 * derived from them — the slug -> activeConfig map used by the listing-card
 * badge, and the parent/child tree used by the category picker. One request
 * serves both, on any page, no matter how many components mount.
 *
 * Caching the *promise* rather than the resolved rows is what collapses a list
 * of 20+ simultaneously-mounting cards into a single request: if the resolved
 * value were cached, all 20 would fire before the first response landed.
 *
 * Cached for the lifetime of the page rather than a timed TTL: an admin config
 * change lands on the next full load, which is the freshness this is specified
 * to have. Nothing here needs to observe a change mid-session.
 */
let cachedRowsPromise = null;

export const fetchCategoryRows = () => {
  if (!cachedRowsPromise) {
    cachedRowsPromise = onGetCategories().catch(error => {
      // Don't cache a failure — a transient error would otherwise suppress the
      // badge, and empty the picker, for the rest of the session. Reset so the
      // next mount retries.
      cachedRowsPromise = null;
      console.error('Failed to load categories:', error);
      return [];
    });
  }
  return cachedRowsPromise;
};

/** Subscribes to the shared rows and derives a view from them. */
const useDerivedCategories = (derive, initial) => {
  const [value, setValue] = useState(initial);

  useEffect(() => {
    let cancelled = false;
    fetchCategoryRows().then(rows => {
      if (!cancelled) setValue(derive(rows));
    });
    return () => {
      cancelled = true;
    };
    // `derive` is intentionally not a dependency: callers pass an inline
    // function, so depending on it would re-run this on every render.
  }, []);

  return value;
};

/** slug -> that category's currently active config. */
export const useCategoryConfigMap = () => useDerivedCategories(buildCategoryConfigMap, {});

/**
 * Parent categories with their `childCategory` arrays, each carrying
 * `activeConfig` and `iconUrl`. Shape matches `organizedCategories` elsewhere.
 *
 * Starts as `null`, not `[]`, so a caller can tell "still loading" from
 * "loaded, nothing active" — those need different empty states.
 */
export const useCategoryTree = () => useDerivedCategories(transformDbCategories, null);

export default useCategoryConfigMap;
